import React, { useEffect, useMemo, useRef, useState } from "react";
import { Editor } from "primereact/editor";
import FeatherIcon from "feather-icons-react";
import { Col, Row } from "reactstrap";
import FloatInput from "src/components/Common/FloatInput";
import * as Yup from "yup";
import { Formik, Form, ErrorMessage } from "formik";
import ErrorText from "src/components/UI/ErrorText";
import {
  formatChatTime,
  formatDate,
  hasAdminOrEmployeeRoleHandler,
  hasDealerRoleHandler,
  hasManufacturerRoleHandler,
} from "src/helpers/common_helpers";
import { useParams } from "react-router";
import { AutoComplete } from "primereact/autocomplete";
import { API } from "src/util/api";
import { useDispatch } from "react-redux";
import { getPendingReasonCCMailListLoading } from "src/store/actions";
import { useSelector } from "react-redux";
import { RootReducerState } from "src/store/reducers";
import { useToast } from 'src/components/Common/ReactToaster';
import ChatLoader from "./ChatLoader";
import FilePreviewList from "./FilePreviewList";
import noSpam from './no-spam.svg';

interface groupChatProps {
  pendingreasonDropdown: any;
  valuesProp: any;
  setFieldValue: any;
  pending: boolean;
}


const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GroupChat = ({ pendingreasonDropdown, valuesProp, setFieldValue, pending }: groupChatProps) => {
  const { id } = useParams();
  const quillRef: any = useRef<any>(null);
  const { showToast } = useToast();
  const dispatch = useDispatch()
  const [messages, setMessages] = useState<any>([]);
  const [addCCButton, setAddCCButton] = useState<boolean>(false);
  const [chatLoading, setChatLoading] = useState<boolean>(true);
  const [emailInput, setEmailInput] = useState("");
  const [filteredEmails, setFilteredEmails] = useState<string[]>([]);
  const { pendingReasonCCMailList, jobsheet, pendingReasonAll } = useSelector(
    (state: RootReducerState) => state.jobsheetReducer
  );
  const [allSuggestions, setAllSuggestions] = useState<string[]>([]);
  const fileInputRef = useRef<any>(null);
  const [selectedFile, setSelectedFile] = useState<any>([]);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [submittedReasonId, setSubmittedReasonId] = useState<any>(null);
  const [localDealerPendingActive, setLocalDealerPendingActive] = useState<boolean>(false);
  useEffect(() => {
    if (pendingReasonCCMailList?.length > 0) {
      setAllSuggestions(pendingReasonCCMailList?.map((data: any) => data.email))
    }
  }, [pendingReasonCCMailList])

  const getChatData = async () => {
    setChatLoading(true)
    try {
      const response = await API.getPendingReasonChatListApi(id)
      if (response.data) {
        setMessages(response.data)
      }
    } catch (error: any) {

    }
    setChatLoading(false)
    debugger
  }
  useEffect(() => {
    if (id) {
      getChatData()
      setIsSubmitted(false)
      setSubmittedReasonId(null)
      setLocalDealerPendingActive(false)
    }
  }, [id])

  useEffect(() => {
    if (jobsheet && hasAdminOrEmployeeRoleHandler()) {
      dispatch(getPendingReasonCCMailListLoading(jobsheet?.jobPrefix))
    }
  }, [jobsheet])
  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem("authUser") || "{}");

  const SPECIAL_REASONS = [
    "Awaiting for Necessory information",
    "Awaiting for Necessary information",
    "Awaiting for Confirmation from Dealer",
  ];

  const DEALER_SPECIAL_REASONS = [
    "Awaiting for Confirmation from Dealer",
    "Awaiting for Necessory information",
    "Awaiting for Necessary information"
  ];

  const MANUFACTURER_SPECIAL_REASONS = [
    "Awaiting Supplier Approval / Advice",
    "Awaiting for updated Software release"
  ];

  const normalize = (str: string) => str?.replace(/\s+/g, " ").trim().toLowerCase() || "";

  // Group messages by date
  const groupedMessages = useMemo(() => {
    return messages?.reduce((groups: any, msg: any) => {
      const dateKey = formatDate(msg.time);
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(msg);
      return groups;
    }, {});
  }, [messages]);

  const lastPendingReason = useMemo(() => {
    if (!messages || messages.length === 0) return null;
    const filteredMessages = messages.filter((msg: any) =>
      ["Admin", "Employee"].includes(msg.userType) && msg.pendingReason
    );
    if (filteredMessages.length === 0) return null;
    return filteredMessages[filteredMessages.length - 1];
  }, [messages]);

  const isSpecialPending = useMemo(() => {
    const currentReason = normalize(lastPendingReason?.pendingReason || "");
    return (
      pending &&
      lastPendingReason &&
      SPECIAL_REASONS.some((r) => normalize(r) === currentReason) && hasAdminOrEmployeeRoleHandler()
    );
  }, [pending, lastPendingReason]);

  const isReplyingToSpecial = useMemo(() => {
    return isSpecialPending || localDealerPendingActive;
  }, [isSpecialPending, localDealerPendingActive]);

  const lastPendingReasonId = useMemo(() => {
    if (!lastPendingReason?.pendingReason) return null;
    const normalizedLastReason = normalize(lastPendingReason.pendingReason);

    // 1. Try finding it by ID on the last message
    if (lastPendingReason.pendingReasonId) {
      return lastPendingReason.pendingReasonId;
    }

    // 2. Try finding it in the active dropdown options
    const foundInDropdown = pendingreasonDropdown?.find(
      (val: any) => normalize(val.label) === normalizedLastReason
    )?.value;
    if (foundInDropdown) return foundInDropdown;

    // 3. Fallback to all loaded pending reasons (if active list is filtered/cleared)
    const foundInAll = pendingReasonAll?.find(
      (val: any) => normalize(val.name) === normalizedLastReason
    )?.id;

    return foundInAll || null;
  }, [lastPendingReason, pendingreasonDropdown, pendingReasonAll]);

  // Dynamically append the current pending reason to the options so the dropdown component can bind it
  const dropdownOptions = useMemo(() => {
    let options = pendingreasonDropdown || [];
    if (jobsheet?.type === "TV") {
      options = options.filter((reason: any) => {
        const normalizedName = normalize(reason.label);
        const isDealerReason = DEALER_SPECIAL_REASONS.some(r => normalize(r) === normalizedName);
        const isSupplierReason = MANUFACTURER_SPECIAL_REASONS.some(r => normalize(r) === normalizedName);
        return !isDealerReason && !isSupplierReason;
      });
    }

    if (!isReplyingToSpecial) {
      return options;
    }

    const reasonToBind = isSpecialPending ? lastPendingReason?.pendingReason : (
      pendingreasonDropdown?.find((val: any) => val.value == submittedReasonId)?.label ||
      pendingReasonAll?.find((val: any) => val.id == submittedReasonId)?.name
    );

    if (!reasonToBind) return options;
    const normalizedLastReason = normalize(reasonToBind);
    const exists = options?.some(
      (val: any) => normalize(val.label) === normalizedLastReason
    );
    if (exists) return options;

    const newOption = {
      value: isSpecialPending ? lastPendingReasonId : submittedReasonId,
      label: reasonToBind,
    };
    return [...(options || []), newOption];
  }, [pendingreasonDropdown, lastPendingReason, lastPendingReasonId, isSpecialPending, isReplyingToSpecial, submittedReasonId, pendingReasonAll, jobsheet]);

  const renderMessage = (msg: any) => {
    let pendingReasonUsers = ["Admin", "Employee"]
    const direction = msg.currentUserId === currentUser.uid ? "right" : "left";

    return (
      <div key={msg.time} className={`message-${direction}`}>
        <div className={`message-card ${direction} ${msg.pendingReason ? "pr" : ''}`}>
          {msg.pendingReason && pendingReasonUsers.includes(msg.userType) && (
            <div className="chat-card-header">{msg.pendingReason}</div>
          )}

          {msg.chat && <div className="chat-card-body" dangerouslySetInnerHTML={{ __html: msg.chat }}>
            {/* {msg.chat} */}
          </div>}
          {msg?.chatDocumentDto?.length ? (
            <div className="file-show-list">
              {msg?.chatDocumentDto?.map((chatDocument: any, index: any) => {
                return (
                  <div className="file-content" key={index} onClick={() => onFileClick(chatDocument?.documentId)}>
                    <div className="icon">
                      {getFileIcon(chatDocument?.mimeType)}
                    </div>
                    {chatDocument?.documentName}
                  </div>
                )
              })}

            </div>
          ) : null}
        </div>
        <div className="message-meta">
          <span className="author-name">{msg.name}</span>
          <span className="time">{formatChatTime(msg.time)}</span>
        </div>
      </div>
    );
  };

  const onFileClick = async (fileId: any) => {
    let { data } = await API.getProofDoucumentLinkApi(fileId)
    debugger
    if (data) {
      openDocumet(data)
    }
  }
  const openDocumet = (link: any) => {
    if (link != "" && link != undefined && link != null) {
      const newWindow = window.open(link);
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        alert('Popup blocked! Please allow popups for this site to view the document.');
      } else {
        newWindow.focus();
      }
    }
  }
  const handleFileChange = (e: any) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFile((prev: any) => [...prev, ...files]);
      console.log("Selected files:", files);
    }
  };


  const initialValues: any = useMemo(() => {
    const bindId = isReplyingToSpecial ? (isSpecialPending ? lastPendingReasonId : submittedReasonId) : "";
    return {
      message: "",
      pendingReason: bindId,
      pendingReasonId: bindId || 0,
      ccMails: [] as string[],
    };
  }, [isReplyingToSpecial, isSpecialPending, lastPendingReasonId, submittedReasonId]);

  const isAdmin = hasAdminOrEmployeeRoleHandler();
  const isDealer = hasDealerRoleHandler();
  const isManufacturer = hasManufacturerRoleHandler();

  const validationSchema = Yup.object().shape({
    pendingReason: Yup.string().required("Pending reason is required"),
    message: Yup.string()
      .test("is-non-empty", "Message Content is required", (value) => {
        if (!value) return false;
        const textContent = value.replace(/<[^>]*>/g, "").trim();
        return textContent.length > 0;
      })
      .required("Message Content is required"),
  });
  const convertFileIntoPayload = (fileArray: any) => {
    let data = fileArray?.map((data: any) => {
      return {
        "id": 0,
        "jobId": Number(id),
        "proofName": data?.name,
        "userId": JSON.parse(localStorage.getItem("authUser") || "")?.uid,
        "file": "",
        "fileContentType": data.type,
        "fileName": data.name,
        "isDealerUpdate": false,
        "documentDetail": {
          "fileSize": data.size,
          "mimeType": data.type,
          "name": data?.name
        }
      }
    })
    return data
  }
  const handleSubmit = (values: any, reset: any, setFieldTouched: any, setFieldError: any) => {
    debugger
    setChatLoading(true)
    setSubmittedReasonId(values.pendingReason);
    const newMessage = {
      JobId: id,
      PendingComments: values.message,
      pendingReason: isReplyingToSpecial ? null : (pendingreasonDropdown?.find((val: any) => val.value == values.pendingReason)?.label || ""),
      pendingReasonId: isReplyingToSpecial ? null : (values.pendingReason || null),
      InPending: (hasAdminOrEmployeeRoleHandler() ? true : false),
      CommentAt: new Date().toISOString(),
      chatCcMail: values.ccMails?.length > 0 ? values.ccMails?.map((data: any) => ({ cc: data })) : [],
      IsApproved: false,
      IsReplay: hasDealerRoleHandler() ? true : false,
      isUploadFile: selectedFile?.length > 0 ? true : false,
      isMountReplay: isReplyingToSpecial,
      // chatToMail:[],
      createOrUpdateAJobProofDocumentDtoList: selectedFile?.length > 0 ? convertFileIntoPayload(selectedFile) : []
    };

    setFieldError("pendingReason", "");
    setFieldError("message", "");
    setFieldTouched("pendingReason", false);
    setFieldTouched("message", false);
    handleApiSubmit(newMessage)
    reset()
  };

  const handleApiSubmit = async (formattedData: any) => {
    debugger
    try {
      const response: any = await API.sendNewPendingReasonChatApi(formattedData)
      console.log(response)
      setFieldValue('pendingCommentsDtoNew[0].pendingReasonId', formattedData.pendingReasonId)
      setFieldValue('pendingCommentsDtoNew[0].pendingInformation', formattedData.PendingComments)
      if (response?.data?.length > 0) {
        for (let i = 0; i < response?.data?.length; i++) {
          let fileData = response?.data[i]
          if (fileData?.fileUploadUrl) {
            const fileResponse = await uploadFile(fileData, selectedFile[i]);
          }
        }
      }
      if (response) {
        getChatData()
        if (formattedData.pendingReasonId) {
          showToast(`Pending send successfully`, { type: "success" });
          setIsSubmitted(true);
          const reasonLabel = pendingReasonAll?.find((val: any) => val.id == formattedData.pendingReasonId)?.name 
            || pendingreasonDropdown?.find((val: any) => val.value == formattedData.pendingReasonId)?.label;
          const isSubmittedSpecial = SPECIAL_REASONS.some((r) => normalize(r) === normalize(reasonLabel || ""));
          if (isSubmittedSpecial) {
            setLocalDealerPendingActive(true);
          }
        }
      }
      setTimeout(() => {
        try {
          const resp = API.sendNewPendingReasonMosChatApi(formattedData)
        } catch (error: any) { }
      }, 1000)
    } catch (error: any) {

    } finally {
      setSelectedFile([])
      setChatLoading(false)
    }
  }

  const uploadFile = async (fileData: any, selectedFile: any) => {
    try {
      const fileUploadResponse = await fetch(fileData.fileUploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": selectedFile.type,
        },
        body: selectedFile,
      });
      if (fileUploadResponse?.ok) {
        const successResponse = await API.chatFileUploadSuccessApi(fileData?.proofId)
      }
      if (!fileUploadResponse.ok) {
        throw new Error(`Upload failed with status ${fileUploadResponse.status}`);
      }

      return fileUploadResponse; // or await fileUploadResponse.json() if it returns JSON
    } catch (error) {
      console.error("File upload error:", error);
      throw error;
    }
  };



  const bottomRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [groupedMessages]);

  return (
    <React.Fragment>

      <Row className="mt-2">
        <div className="dealer-title-header font-size-15 border-bottom">
          <span className="pull-left">Job Sheet Present Status</span>
        </div>
      </Row>
      <Formik
        onSubmit={(e: any) => {
          e.stopPropagation(); // prevent bubbling to outer form
          e.preventDefault();
          debugger // stop browser reload
          // handleSubmit();
        }}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ values, setFieldValue, handleChange, errors, handleBlur, validateForm, resetForm, setFieldTouched, setFieldError, touched }: any) => {

          const searchEmail = (event: any) => {
            const query = event.query.trim().toLowerCase();
            if (!query) {
              // Show only emails not already selected
              const availableEmails = allSuggestions.filter(
                (email) => !values.ccMails.includes(email)
              );
              setFilteredEmails(availableEmails);
              return;
            }

            // Filter and exclude already selected ones
            const filtered = allSuggestions.filter(
              (email) =>
                email.toLowerCase().includes(query) &&
                !values.ccMails.includes(email)
            );
            setFilteredEmails(filtered);
          };
          return (
            <Form>
              {/* Pending Reason Dropdown */}
              <Row>
                <Col xl={4} lg={5} md={7} sm={12} className="mt-mb">
                  {isAdmin && valuesProp.inPending && (
                    <FloatInput
                      type="dropdown"
                      name="pendingReason"
                      label="Pending reason"
                      onChange={handleChange}
                      value={values.pendingReason}
                      options={dropdownOptions}
                      placeholder="Select Pending Reason"
                      required
                      className="w-100"
                      disabled={pending || isSubmitted}
                      onBlur={handleBlur}
                      invalid={
                        errors.pendingReason && touched?.pendingReason
                          ? true
                          : false
                      }
                    />
                  )}
                </Col>
                <Col xl={8} lg={7} md={12} sm={12} className="">
                  {/* Chat Section */}
                  <div className="main-area">
                    <div className="conversation-container">
                      {chatLoading && <ChatLoader />}
                      {(Object.entries(groupedMessages)?.length == 0 && !chatLoading) && <NoMessagesView />}
                      {Object.entries(groupedMessages).map(([date, msgs]: any) => (
                        <div key={date}>
                          <div className="date-section">
                            <div className="date-header">{date}</div>
                          </div>
                          {msgs.length > 0 ? msgs?.map(renderMessage) : "No Chats Found"}
                        </div>
                      ))}
                      <div ref={bottomRef} />
                    </div>
                  </div>
                  {/* Email Section */}
                  <div className="chat-form-area">

                    <FilePreviewList files={selectedFile} setSelectedFile={setSelectedFile} isLoading={chatLoading} />
                    {/* Message Editor */}
                    {((valuesProp.inPending && !hasAdminOrEmployeeRoleHandler()) || ((!pending || isSpecialPending) && hasAdminOrEmployeeRoleHandler())) && (
                      <Col lg={12} className="mt-2 chat-box d-flex gap-3">
                        {/* <label htmlFor="message">
                  Message<span className="text-danger ml-05">*</span>
                </label> */}
                        {/* <Editor
                  id="message"
                  name="message"
                  value={values.message}
                  onTextChange={(e) =>{setFieldTouched("message",true); setFieldValue("message", e.htmlValue)}}
                  style={{ height: "100px" }}
                  className={errors["message"] ? "is-invalid-editor" : ""}
                  headerTemplate={toolbar}
                  onFocus={()=>setFieldTouched("message",true)}
                  onBlur={handleBlur}
                  ref={quillRef}
                  onPaste={(e: any) => handlePaste(e)}
                />
                <ErrorMessage name={"message"} component={ErrorText} /> */}
                        <div className="file-textarea">
                          <textarea
                            placeholder="Type a message..."
                            className="chat-text-area"
                            value={values.message}
                            name={"message"}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <div className="" style={{ position: "absolute", bottom: "-12px" }}><ErrorMessage name={"message"} component={ErrorText} /></div>

                          <span className="file-upload-button">
                            <button
                              type="button"
                              onClick={() => fileInputRef.current.click()}
                              className="ql-custom-upload"
                              style={{ border: "none", background: "transparent", cursor: "pointer" }}
                            >
                              <FeatherIcon icon="paperclip" />
                            </button>
                          </span>
                        </div>
                        <div className="text-end button-chat">
                          <div>


                            {/* hidden file input */}
                            <input
                              type="file"
                              ref={fileInputRef}
                              multiple
                              onChange={handleFileChange}
                              style={{ display: "none" }}
                            />
                          </div>
                          {valuesProp.inPending && (
                            <button
                              className="btn send-button"
                              type="button"
                              disabled={chatLoading}
                              onClick={async () => {
                                // ✅ Manually trigger validation first
                                setFieldTouched("pendingReason", true);
                                setFieldTouched("message", true);
                                const formErrors = await validateForm();
                                debugger
                                if (Object.keys(formErrors).length === 0) {
                                  // ✅ No errors, submit safely
                                  handleSubmit(
                                    values,
                                    resetForm,
                                    setFieldTouched,
                                    setFieldError
                                  );
                                } else {
                                  // ❌ There are validation errors
                                }
                              }}
                            >
                              <FeatherIcon icon="send" className="" />
                            </button>
                          )}
                        </div>
                      </Col>
                    )}
                    {((valuesProp.inPending && !hasAdminOrEmployeeRoleHandler()) || ((!pending || isSpecialPending) && !isSubmitted && hasAdminOrEmployeeRoleHandler())) && (
                      <div className="email-section">
                        {addCCButton && (
                          <AutoComplete
                            multiple
                            value={values.ccMails}
                            suggestions={filteredEmails}
                            completeMethod={searchEmail}
                            onChange={(e) => {
                              // Validate last typed email
                              const last = e.value[e.value.length - 1];
                              if (last && !emailRegex.test(last)) {
                                e.value.pop();
                                return;
                              }
                              setFieldValue("ccMails", e.value);
                            }}
                            placeholder="Search and select CC emails..."
                            dropdown
                            className="w-100"
                            emptyMessage="No cc email is available"
                            showEmptyMessage={true}
                          />
                        )}
                        <button
                          className="add-cc-btn"
                          type="button"
                          onClick={() => { setAddCCButton((prev) => !prev); setFieldValue('ccMails', []) }}
                        >
                          <FeatherIcon icon="mail" />
                          {addCCButton ? "Remove CC Mails" : " Add More CC Mails"}
                        </button>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </React.Fragment>
  );
};

export default GroupChat;


export const getFileIcon = (mimeType: any) => {
  const imageMimeTypes = [
    "image/jpg", "image/jpeg", "image/png", "image/gif",
    "image/bmp", "image/webp", "image/avif", "image/tiff", "image/svg+xml"
  ];
  const pdfMimeTypes = ["application/pdf"];
  const excelMimeTypes = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv"
  ];
  const videoMimeTypes = [
    "video/mp4", "video/quicktime", "video/x-msvideo", "video/x-ms-wmv",
    "video/mpeg", "video/x-flv", "video/webm", "video/ogg",
    "video/3gpp", "video/3gpp2"
  ];

  if (imageMimeTypes.includes(mimeType)) {
    return <FeatherIcon icon="image" />;
  } else if (pdfMimeTypes.includes(mimeType)) {
    return <FeatherIcon icon="file-text" />;
  } else if (excelMimeTypes.includes(mimeType)) {
    return <FeatherIcon icon="file" />;
  } else if (videoMimeTypes.includes(mimeType)) {
    return <FeatherIcon icon="video" />;
  } else {
    return <FeatherIcon icon="file" />; // default icon
  }
};

const NoMessagesView = () => {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "center" }}>
      <img src={noSpam} style={{ width: "100px" }} />
      <div>No Messages Yet</div>
    </div>
  )
}